import React, { createContext, useContext, useMemo, useState } from "react";

const join = (...parts) => parts.filter(Boolean).join(" ");

const mapColor = (color) => {
  if (!color) return "";
  return color === "none" ? "" : `btn-${color}`;
};

export const MDBContainer = ({ fluid, className, children, ...props }) => (
  <div className={join(fluid ? "container-fluid" : "container", className)} {...props}>
    {children}
  </div>
);

export const MDBRow = ({ className, children, ...props }) => (
  <div className={join("row", className)} {...props}>
    {children}
  </div>
);

const buildColClass = (prefix, value) => {
  if (value === undefined || value === null || value === "") return "";
  return typeof value === "number" || /^\d+$/.test(String(value))
    ? `${prefix}-${value}`
    : `${prefix}-${String(value)}`;
};

export const MDBCol = ({ className, md, lg, xl, size, children, tag: Tag = "div", ...props }) => {
  const classes = join(
    "col",
    buildColClass("col", size),
    buildColClass("col-md", md),
    buildColClass("col-lg", lg),
    buildColClass("col-xl", xl),
    className
  );

  return (
    <Tag className={classes} {...props}>
      {children}
    </Tag>
  );
};

export const MDBCard = ({ className, children, ...props }) => (
  <div className={join("card", className)} {...props}>
    {children}
  </div>
);

export const MDBCardBody = ({ className, children, ...props }) => (
  <div className={join("card-body", className)} {...props}>
    {children}
  </div>
);

export const MDBCardTitle = ({ className, children, ...props }) => (
  <h5 className={join("card-title", className)} {...props}>
    {children}
  </h5>
);

export const MDBCardText = ({ className, children, ...props }) => (
  <p className={join("card-text", className)} {...props}>
    {children}
  </p>
);

export const MDBBtn = ({
  color = "primary",
  outline,
  floating,
  size,
  className,
  children,
  href,
  tag,
  ...props
}) => {
  const sizeClass = size === "lg" ? "btn-lg" : size === "sm" ? "btn-sm" : "";
  const variant = outline ? `btn-outline-${color}` : mapColor(color) || "btn-primary";
  const classes = join("btn", variant, floating ? "btn-floating" : "", sizeClass, className);
  const Tag = tag || (href ? "a" : "button");
  return (
    <Tag className={classes} href={href} {...props}>
      {children}
    </Tag>
  );
};

export const MDBIcon = ({ icon, fas, fab, className, size, spin, color, ...props }) => {
  const family = fab ? "fab" : fas ? "fas" : "fas";
  const normalized = String(icon || "circle").replace(/\s+/g, "-");
  const sizeClass = size && size !== "sm" ? `fa-${size}` : "";
  const spinClass = spin ? "fa-spin" : "";
  const style = color ? { color } : undefined;
  return <i className={join(family, `fa-${normalized}`, sizeClass, spinClass, className)} style={style} {...props} />;
};

export const MDBNavbar = ({ className, children, expand = "lg", dark, light, bgColor, sticky, ...props }) => {
  const tone = dark ? "navbar-dark" : light ? "navbar-light" : "";
  const bg = bgColor ? `bg-${bgColor}` : "";
  return (
    <nav className={join("navbar", `navbar-expand-${expand}`, tone, bg, sticky ? "sticky-top" : "", className)} {...props}>
      {children}
    </nav>
  );
};

export const MDBNavbarBrand = ({ className, children, link, href, ...props }) => {
  const Tag = link || href ? "a" : "span";
  return (
    <Tag className={join("navbar-brand", className)} href={href} {...props}>
      {children}
    </Tag>
  );
};

export const MDBNavbarToggler = ({ className, children, ...props }) => (
  <button type="button" className={join("navbar-toggler", className)} {...props}>
    {children}
  </button>
);

export const MDBNavbarNav = ({ className, children, right, centered, ...props }) => (
  <ul className={join("navbar-nav", right ? "ms-auto" : "", centered ? "mx-auto" : "", className)} {...props}>
    {children}
  </ul>
);

export const MDBNavbarItem = ({ className, children, ...props }) => (
  <li className={join("nav-item", className)} {...props}>
    {children}
  </li>
);

export const MDBNavbarLink = ({ className, children, ...props }) => (
  <a className={join("nav-link", className)} {...props}>
    {children}
  </a>
);

export const MDBCollapse = ({ className, show, navbar, children, ...props }) => (
  <div className={join(navbar ? "navbar-collapse" : "collapse", show ? "show" : "", className)} {...props}>
    {children}
  </div>
);

export const MDBFooter = ({ className, children, bgColor, ...props }) => (
  <footer className={join(bgColor ? `bg-${bgColor}` : "", className)} {...props}>
    {children}
  </footer>
);

export const MDBInput = ({ label, className, id, ...props }) => (
  <div className="mb-2">
    {label && (
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
    )}
    <input id={id} className={join("form-control", className)} {...props} />
  </div>
);

export const MDBDatepicker = ({ label, className, value, onChange, id, ...props }) => (
  <div className="mb-2">
    {label && (
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
    )}
    <input
      type="date"
      id={id}
      className={join("form-control", className)}
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      {...props}
    />
  </div>
);

export const MDBSelect = ({ label, data = [], value, onChange, className, id, ...props }) => (
  <div className="mb-2">
    {label && (
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
    )}
    <select
      id={id}
      className={join("form-select", className)}
      value={value || ""}
      onChange={(e) => onChange?.({ value: e.target.value })}
      {...props}
    >
      <option value="">Select</option>
      {data.map((opt) => (
        <option key={`${opt.value}-${opt.text}`} value={opt.value}>
          {opt.text}
        </option>
      ))}
    </select>
  </div>
);

export const MDBCheckbox = ({ label, className, value, onChange, id, ...props }) => (
  <div className={join("form-check", className)}>
    <input
      id={id}
      type="checkbox"
      className="form-check-input"
      checked={!!value}
      onChange={(e) => onChange?.({ value: e.target.checked })}
      {...props}
    />
    <label className="form-check-label" htmlFor={id}>
      {label}
    </label>
  </div>
);

export const MDBRadio = ({ label, className, value, onChange, inline, id, name, ...props }) => (
  <div className={join("form-check", inline ? "form-check-inline" : "", className)}>
    <input
      id={id}
      name={name}
      type="radio"
      className="form-check-input"
      value={value}
      onChange={(e) => onChange?.({ value: e.target.value })}
      {...props}
    />
    <label className="form-check-label" htmlFor={id}>
      {label}
    </label>
  </div>
);

export const MDBBadge = ({ color = "secondary", className, children, ...props }) => (
  <span className={join("badge", `bg-${color}`, className)} {...props}>
    {children}
  </span>
);

export const MDBLoadingManagement = ({ fullScreen, className, spinnerElement }) => (
  <div
    className={join("d-flex justify-content-center align-items-center", className)}
    style={fullScreen ? { minHeight: "100vh", width: "100%" } : undefined}
  >
    {spinnerElement || <div className="spinner-border" role="status" />}
  </div>
);

export const MDBToast = ({ open, color = "primary", headerContent, bodyContent, className, onClose, ...props }) => {
  if (!open) return null;
  return (
    <div className={join("toast show border", className)} style={{ minWidth: 280 }} {...props}>
      <div className={join("toast-header", `text-bg-${color}`)}>
        <div className="me-auto">{headerContent}</div>
        <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={onClose} />
      </div>
      <div className="toast-body">{bodyContent}</div>
    </div>
  );
};

const DropdownCtx = createContext(null);

export const MDBDropdown = ({ className, children }) => {
  const [open, setOpen] = useState(false);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return (
    <DropdownCtx.Provider value={value}>
      <div className={join("dropdown", className)}>{children}</div>
    </DropdownCtx.Provider>
  );
};

export const MDBDropdownToggle = ({ className, children, tag, ...props }) => {
  const ctx = useContext(DropdownCtx);
  const Tag = tag || "button";
  return (
    <Tag
      className={join("dropdown-toggle", className)}
      onClick={(e) => {
        e.preventDefault();
        ctx?.setOpen(!ctx.open);
      }}
      aria-expanded={ctx?.open}
      {...props}
    >
      {children}
    </Tag>
  );
};

export const MDBDropdownMenu = ({ className, children, ...props }) => {
  const ctx = useContext(DropdownCtx);
  return (
    <div className={join("dropdown-menu", ctx?.open ? "show" : "", className)} {...props}>
      {children}
    </div>
  );
};

export const MDBDropdownItem = ({ className, children, link, href, onClick, ...props }) => {
  const ctx = useContext(DropdownCtx);
  const Tag = link || href ? "a" : "button";
  return (
    <Tag
      className={join("dropdown-item", className)}
      href={href}
      onClick={(e) => {
        onClick?.(e);
        ctx?.setOpen(false);
      }}
      {...props}
    >
      {children}
    </Tag>
  );
};

const ModalCtx = createContext(null);

export const MDBModal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <ModalCtx.Provider value={{ onClose }}>
      <div className="modal d-block" tabIndex={-1} role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
        {children}
      </div>
    </ModalCtx.Provider>
  );
};

export const MDBModalDialog = ({ className, centered, children, ...props }) => (
  <div className={join("modal-dialog", centered ? "modal-dialog-centered" : "", className)} {...props}>
    {children}
  </div>
);

export const MDBModalContent = ({ className, children, ...props }) => (
  <div className={join("modal-content", className)} {...props}>
    {children}
  </div>
);

export const MDBModalHeader = ({ className, children, ...props }) => (
  <div className={join("modal-header", className)} {...props}>
    {children}
  </div>
);

export const MDBModalTitle = ({ className, children, ...props }) => (
  <h5 className={join("modal-title", className)} {...props}>
    {children}
  </h5>
);

export const MDBModalBody = ({ className, children, ...props }) => (
  <div className={join("modal-body", className)} {...props}>
    {children}
  </div>
);

export const MDBModalFooter = ({ className, children, ...props }) => (
  <div className={join("modal-footer", className)} {...props}>
    {children}
  </div>
);

export const MDBTypography = ({ tag: Tag = "p", className, children, ...props }) => (
  <Tag className={className} {...props}>
    {children}
  </Tag>
);

export const MDBAutocomplete = ({
  data = [],
  label,
  onSearch,
  onSelect,
  displayValue = (row) => row?.title || "",
  itemContent,
  className,
  style,
}) => {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);

  return (
    <div className={join("position-relative", className)} style={style}>
      {label && <label className="form-label">{label}</label>}
      <input
        className="form-control"
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          setValue(next);
          setOpen(true);
          onSearch?.(next);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && Array.isArray(data) && data.length > 0 && (
        <div className="list-group position-absolute w-100" style={{ zIndex: 1060, maxHeight: 240, overflowY: "auto" }}>
          {data.map((item, idx) => (
            <button
              key={`${displayValue(item)}-${idx}`}
              type="button"
              className="list-group-item list-group-item-action"
              onClick={() => {
                setValue(displayValue(item));
                setOpen(false);
                onSelect?.(item);
              }}
            >
              {itemContent ? itemContent(item) : displayValue(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
